import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../../../lib/supabase'
import { useAuth } from '../../../hooks/useAuth'
import { uploadFile, deleteFile } from '../../../lib/upload'

export const useEventPhotos = (eventId) => {
  const { household } = useAuth()
  const qc  = useQueryClient()
  const key = ['event-photos', eventId]

  const query = useQuery({
    queryKey: key,
    enabled:  !!eventId && !!household?.id,
    queryFn:  async () => {
      const { data, error } = await supabase
        .from('event_photos')
        .select('id, file_path, file_name, file_size, caption, taken_at, uploaded_by, created_at')
        .eq('event_id', eventId)
        .order('created_at')
      if (error) throw error
      return data ?? []
    },
  })

  const { user } = useAuth()

  const addPhotos = useMutation({
    mutationFn: async (files) => {
      const results = await Promise.allSettled(
        files.map(async (file) => {
          const ext  = file.name.split('.').pop() || 'jpg'
          const path = `${household.id}/${user.id}/${eventId}/${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`
          const { path: storedPath, fileSize, mimeType } = await uploadFile(
            file, 'household-photos', path, household.id,
            { withThumbnail: true }
          )
          const { error } = await supabase.from('event_photos').insert({
            event_id:    eventId,
            household_id: household.id,
            uploaded_by: user.id,
            file_path:   storedPath,
            file_name:   file.name,
            file_size:   fileSize,
          })
          if (error) {
            await deleteFile('household-photos', storedPath).catch(() => {})
            throw error
          }
        })
      )
      const failed = results.filter((r) => r.status === 'rejected')
      if (failed.length === files.length) throw new Error('All uploads failed')
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  })

  const updateCaption = useMutation({
    mutationFn: async ({ id, caption }) => {
      const { error } = await supabase.from('event_photos').update({ caption }).eq('id', id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  })

  const removePhoto = useMutation({
    mutationFn: async ({ id, filePath }) => {
      await deleteFile('household-photos', filePath)
      const { error } = await supabase.from('event_photos').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  })

  return { ...query, addPhotos, updateCaption, removePhoto }
}
